// IndexedDB utility for storing File objects
// This allows File objects to persist across page loads/navigation

const DB_NAME = 'EnvelopeDocumentsDB';
const DB_VERSION = 1;
const STORE_NAME = 'documents';

// Open or create the IndexedDB database
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'documentId' });
      }
    };
  });
};

// Save files for a document
export const saveFilesToIndexedDB = async (documentId, files) => {
  try {
    // FIRST: Convert all files to ArrayBuffer BEFORE opening transaction
    // (Transactions auto-close during async operations)
    const filesData = await Promise.all(
      files.map(async (file, index) => {
        let fileData = null;
        if (file.file instanceof File) {
          fileData = await file.file.arrayBuffer();
        }
        
        return {
          index,
          id: file.id,
          name: file.name,
          kb: file.kb,
          path: file.path,
          hasFile: file.file !== null,
          fileData: fileData,
          fileType: file.file?.type || 'application/pdf',
          fileName: file.file?.name || file.name,
        };
      })
    );
    
    // THEN: Open transaction and save (no async operations here)
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    const record = {
      documentId: String(documentId),
      files: filesData,
      timestamp: Date.now(),
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(record);
      request.onsuccess = () => {
        console.log('Files saved to IndexedDB for document:', documentId);
        resolve(true);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error saving files to IndexedDB:', error);
    return false;
  }
};

// Load files for a document
export const loadFilesFromIndexedDB = async (documentId) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const request = store.get(String(documentId));
      
      request.onsuccess = () => {
        const record = request.result;
        if (!record) {
          console.log('No files found in IndexedDB for document:', documentId);
          resolve(null);
          return;
        }
        
        // Convert stored data back to File objects
        const files = record.files.map((fileData) => {
          let file = null;
          if (fileData.hasFile && fileData.fileData) {
            // Recreate File object from ArrayBuffer
            const blob = new Blob([fileData.fileData], { type: fileData.fileType });
            file = new File([blob], fileData.fileName, { type: fileData.fileType });
          }
          
          return {
            id: fileData.id,
            name: fileData.name,
            kb: fileData.kb,
            path: fileData.path,
            file: file,
          };
        });
        
        console.log('Files loaded from IndexedDB for document:', documentId, files);
        resolve(files);
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error loading files from IndexedDB:', error);
    return null;
  }
};

// Delete files for a document
export const deleteFilesFromIndexedDB = async (documentId) => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const request = store.delete(String(documentId));
      request.onsuccess = () => {
        console.log('Files deleted from IndexedDB for document:', documentId);
        resolve(true);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error deleting files from IndexedDB:', error);
    return false;
  }
};

// Clear all stored documents (optional utility)
export const clearAllFilesFromIndexedDB = async () => {
  try {
    const db = await openDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => {
        console.log('All files cleared from IndexedDB');
        resolve(true);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error clearing IndexedDB:', error);
    return false;
  }
};
